import { HttpHandler, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, lastValueFrom } from 'rxjs';
import { ServiceResponse } from 'src/app/@shared/interfaces/service-response.interface';
import { HttpClientService } from 'src/app/@shared/services/http-client/http-client.service';
import { Address } from '../interfaces/location/address.interface';
import { Coordinate } from '../interfaces/location/coordinate.interface';
import { environment } from 'src/environments/environment';
import { City } from '../interfaces/location/city.interface';
import { ParkAndRide } from '../interfaces/location/park-and-ride.interface';
import { POIRequest } from '../components/route-planner-form/forms/source-destination-form/request-poi/request-poi.component';
import { PointOfInterest } from '../interfaces/location/point-of-interest.interface';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { MapLocations } from '../interfaces/location/map-locations.interface';
import { RavKavStation } from '../interfaces/location/ravkav-station.interface';
import { StopDetails } from '../interfaces/location/stop-details.interface';

@Injectable({
  providedIn: 'root',
})
export class LocationRepository {
  constructor(
    private readonly httpClientService: HttpClientService,
    httpHandler: HttpHandler,
    languageService: LanguageService
  ) {
    this.httpClientService = new HttpClientService(
      httpHandler,
      languageService
    );
    this.httpClientService.apiUrl = environment.location_service_url;
  }

  searchCity(cityTerm: string): Observable<ServiceResponse<City[]>> {
    return this.httpClientService.mot_get<ServiceResponse<City[]>>(
      'location/SearchCity',
      new HttpParams().append('cityTerm', cityTerm)
    );
  }

  searchAddress(addressTerm: string): Observable<ServiceResponse<Address[]>> {
    return this.httpClientService.mot_get<ServiceResponse<Address[]>>(
      'location/SearchAddress',
      new HttpParams().append('addressTerm', addressTerm)
    );
  }

  getMapLocations(): Observable<ServiceResponse<MapLocations>> {
    return this.httpClientService.mot_get<ServiceResponse<MapLocations>>(
      'location/GetMapLocations'
    );
  }

  getAddressDataByCoordinates(
    coordinates: Coordinate
  ): Observable<ServiceResponse<Address>> {
    return this.httpClientService.mot_get<ServiceResponse<Address>>(
      'location/GetAddressDataByCoordinates',
      new HttpParams()
        .append('lat', coordinates.lat)
        .append('lng', coordinates.lng)
    );
  }

  getParkAndRideLots(): Observable<ServiceResponse<ParkAndRide[]>> {
    return this.httpClientService.mot_get('Location/GetParkAndGoLots');
  }

  sendPointOfInterest(data: POIRequest): Observable<ServiceResponse<string>> {
    return this.httpClientService.mot_post(
      'Location/AddPointOfInterestRequest',
      data
    );
  }

  getPointsOfInterest(): Observable<ServiceResponse<PointOfInterest[]>> {
    return this.httpClientService.mot_get('Location/GetPointsOfInterest');
  }

  getPointOfInterestById(
    id: number
  ): Observable<ServiceResponse<PointOfInterest>> {
    return this.httpClientService.mot_get(
      'Location/GetPointOfInterestById',
      new HttpParams().append('id', id)
    );
  }

  getRavKavStations(): Observable<ServiceResponse<RavKavStation[]>> {
    return this.httpClientService.mot_get<ServiceResponse<RavKavStation[]>>(
      'location/GetMapRavKavStations'
    );
  }

  getMapParkAndGoLots(): Observable<ServiceResponse<ParkAndRide[]>> {
    return this.httpClientService.mot_get<ServiceResponse<ParkAndRide[]>>(
      'location/GetMapParkAndGoLots'
    );
  }

  getMapStops(): Observable<ServiceResponse<StopDetails[]>> {
    const batchSize = 30000;

    return new Observable<ServiceResponse<StopDetails[]>>((observer) => {
      const fetchAll = async () => {
        const allStops: StopDetails[] = [];
        let skip = 0;

        while (true) {
          const params = new HttpParams()
            .append('skip', skip)
            .append('take', batchSize);

          const response = await lastValueFrom(
            this.httpClientService.mot_get<ServiceResponse<StopDetails[]>>(
              'location/GetMapStops',
              params
            )
          );

          if (!response.success) {
            observer.next(response);
            observer.complete();
            return;
          }

          const batch = response.data ?? [];
          allStops.push(...batch);

          if (batch.length < batchSize) {
            break;
          }

          skip += batchSize;
        }

        observer.next({ data: allStops, success: true, message: '' });
        observer.complete();
      };

      fetchAll().catch((err) => observer.error(err));
    });
  }
}
